import React, { useState, useEffect } from 'react';
import { Modal, Table, Button, Form } from 'react-bootstrap';
import { FormattedMessage, useIntl } from 'react-intl';
import _ from 'lodash';

import { deleteNetwork, deleteNetworksGroup, getNetworksGroupDetails } from 'app/crud/networks.crud';
import { deleteTunnel } from 'app/crud/vpn_tunnels.crud';
import { deleteAgentProfile } from 'app/crud/agent_profiles.crud';
import {
  objectDatas,
  networkObjectDatas,
  serviceObjectDatas,
  recordObjectDatas,
} from 'utils/utils';
import NoDataDisplay from 'components/commons/NoDataDisplay';
import TableLoader from 'components/commons/TableLoader';
import BeSafeButton from 'components/commons/BeSafeButton';
import BeSafeTooltip from 'components/commons/BeSafeTooltip';
import BeSafeSearchBar from 'components/commons/BeSafeSearchBar';
import AddObjectModal from './AddObjectModal';
import { successToast, errorToast, warningToast } from 'utils/toastUtils';
import { ConfirmationPopup } from 'components/ConfirmDialog';
import { Add, Delete, Edit } from '@mui/icons-material';
import BeSafeSeparator from 'components/commons/BeSafeSeparator';
import { useFetchNetworkListItems } from 'hooks/useNetworkListItems';
import useAgentlessAvailability from 'hooks/useAgentlessAvailability';
import { shallowEqual, useSelector } from 'react-redux';
import { userNameWithOrigin } from 'utils/userOrigin';

const NetworksListSelect = (props) => {
  const agentlessEnabled = useAgentlessAvailability().enabled;
  const [selectedItems, setSelectedItems] = useState(
    props.initialSelected || []
  );
  const [selectedType, setSelectedType] = useState(
    props.objectType ||
      (props.allowedNetworks.length > 0 ? props.allowedNetworks[0] : '')
  );
  const [allowedNetworks, setAllowedNetworks] = useState([]);
  const [query, setQuery] = useState('');
  const [editMode, setEditMode] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [selectedObject, setSelectedObject] = useState({});
  const usedList = [];

  const intl = useIntl();

  const { items, loading, fetchData } = useFetchNetworkListItems(
    selectedType,
    props,
    query
  );

  const { accountID } = useSelector(
    ({ auth }) => ({
      accountID: auth.user.account_id,
    }),
    shallowEqual
  );

  const selectRows = (item) => {
    if (
      _.findIndex(selectedItems, (i) => {
        return i.id === item.id;
      }) >= 0
    ) {
      setSelectedItems((prev) =>
        selectedItems.filter((si) => si.id !== item.id)
      );
    } else {
      if (props.limit !== 0 && selectedItems.length >= props.limit) {
        return;
      }
      setSelectedItems((prev) => [...prev, item]);
    }
  };

  const handleSubmit = () => {
    if (selectedItems.some((item) => item?.warning)) {
      ConfirmationPopup({
        title: intl.formatMessage({
          id: 'GENERAL.WARNING',
        }),
        description: intl.formatMessage({
          id: 'ZTNA_SOURCES.CONFIRM_EMPTY_MAC',
        }),
        okLabel: intl.formatMessage({
          id: 'GENERAL.OK',
        }),
        cancelLabel: intl.formatMessage({
          id: 'GENERAL.CANCEL',
        }),
        okAction: () => {
          if (_.isFunction(props.changeItems)) {
            props.changeItems(selectedItems);
            handleHide();
          }
        },
        // Cancelling must undo the tick, or the flagged user stays selected and
        // the next save both re-raises this popup and adds them anyway.
        closeAction: () => {
          setSelectedItems((prev) => prev.filter((item) => !item?.warning));
        },
      });
    } else {
      if (_.isFunction(props.changeItems)) {
        props.changeItems(selectedItems);
        handleHide();
      }
    }
  };

  const handleHide = () => {
    setQuery('');
    setSelectedItems([]);
    props.onHide();
  };

  useEffect(() => {
    if (props.showModal && props.initialSelected)
      setSelectedItems(props.initialSelected);
  }, [props.showModal, props.initialSelected]);

  useEffect(() => {
    if (props.showModal) setQuery('');
  }, [props.showModal]);

  const changeObject = (e) => {
    setSelectedType(e.target.value);
  };

  function getAllowedNetworksText(selectType, item) {
    if (selectType.match(/^(source|destination)$/)) {
      return networkObjectDatas[item]?.text;
    } else if (selectType.match(/^service$/)) {
      return serviceObjectDatas[item]?.text;
    } else if (selectType.match(/^record$/)) {
      return recordObjectDatas[item]?.text;
    } else if (selectType === 'tcp_service') {
      return serviceObjectDatas['tcp_service']?.text;
    } else {
      return '';
    }
  }

  const selectItemOnClick = (item) => {
    const prevItems = [...selectedItems];
    if (_.includes(prevItems, item)) {
      const newItems = _.without(prevItems, item);
      setSelectedItems(newItems);
    } else {
      if (props.limit !== 0 && selectedItems.length >= props.limit) {
        return;
      }
      setSelectedItems([...prevItems, item]);
    }
  };

  const getItemName = (item) => {
    if (['network', 'network_range'].includes(item.type)) {
      if (item?.data?.network_type === 'lan') {
        return `${
          item.name +
          ', ' +
          intl.formatMessage({ id: 'NETWORK_OBJECTS.TYPE' }) +
          ': LAN'
        }`;
      } else {
        return `${
          item.name +
          ', ' +
          intl.formatMessage({ id: 'NETWORK_OBJECTS.TYPE' }) +
          ': No-LAN'
        }`;
      }
    }

    if (item.type === 'mac') {
      return `${
        item.name +
        ', ' +
        intl.formatMessage({ id: 'NETWORK_OBJECTS.MAC_ADDRESS' }) +
        `: ${item.target}`
      }`;
    }
    if (item.type === 'user') return userNameWithOrigin(item);
    return item.name;
  };

  useEffect(() => {
    // 'portal' (agentless users) only when the feature is available - one chokepoint for every picker
    const networks = agentlessEnabled
      ? props.allowedNetworks
      : props.allowedNetworks.filter((item) => item !== 'portal');
    setAllowedNetworks(networks);
    setSelectedType(
      props.objectType || (networks.length > 0 ? networks[0] : '')
    );
  }, [props.allowedNetworks, props.objectType, agentlessEnabled]);

  const getTitle = (tag) => {
    let title = 'GENERAL.DATA';

    if (tag === 'portrange') {
      title = 'NETWORK_OBJECTS.PORT';
    } else if (tag.includes('target')) {
      title = 'NETWORK_OBJECTS.IP';
    }

    return title;
  };

  const getTooltip = (item, tags) => {
    let tooltips = [];

    tags.forEach((tag) => {
      const title = getTitle(tag);
      const obj = tag.includes('target') ? item[tag] : item.data?.[tag];

      if (obj) {
        tooltips.push(`${intl.formatMessage({ id: title })}: ${obj}`);
      }
    });

    if (tooltips.length === 0) {
      return '';
    }

    return (
      <>
        {tooltips.map((tooltip) => (
          <span className="d-block">{tooltip}</span>
        ))}
      </>
    );
  };

  const isItemDisabled = (item) => {
    return item.object_type === 'dns_record' && item.data?.search_engine;
  };

  const showEditObjectModal = async (network) => {
    if (network.group_type) {
      const response = await getNetworksGroupDetails(network.id);
      setSelectedObject(response.data);
    } else {
      setSelectedObject(network);
    }
    setEditMode(true);
    setShowModal(true);
  };

  const showNewObjectModal = () => {
    setEditMode(false);
    setShowModal(true);
  };

  const deleteObject = (item) => {
    if (usedList.length > 0) {
      warningToast({
        body: 'NETWORK_OBJECTS.ALREADY_IN_USE',
        intl: intl,
      });
    } else {
      ConfirmationPopup({
        title: intl.formatMessage({
          id: 'GENERAL.WARNING',
        }),
        description: intl.formatMessage({
          id: 'GENERAL.CONFIRM_DELETE',
        }),
        okLabel: intl.formatMessage({
          id: 'GENERAL.OK',
        }),
        cancelLabel: intl.formatMessage({
          id: 'GENERAL.CANCEL',
        }),
        okAction: () => {
          if (props.cloudSslVpnProfile) {
            deleteTunnel(props.cloudSslVpnProfile, item.id)
              .then(() => {
                fetchData();
                successToast({ body: 'SSLVPN.DELETE_SUCCESS', intl: intl });
              })
              .catch((err) => {
                if (err.response.data.already_in_used) {
                  errorToast({
                    body: 'SSLVPN.ALREADY_IN_USE',
                    intl: intl,
                  });
                } else if (err.response.data.error) {
                  errorToast({
                    body: err.response.data.error,
                    intl: intl,
                  });
                } else {
                  errorToast({ body: 'SSLVPN.DELETE_ERROR', intl: intl });
                }
              });
          } else {
            const delNetwork = item.is_agent_profile
              ? deleteAgentProfile(item.id, accountID)
              : item.object_type
              ? deleteNetwork(item.id)
              : deleteNetworksGroup(item.id);
            delNetwork
              .then(() => {
                fetchData();
                successToast({
                  body: 'NETWORK_OBJECTS.DELETE_SUCCESS',
                  intl: intl,
                });
              })
              .catch((err) => {
                if (err.response.data.already_in_used) {
                  errorToast({
                    body: 'NETWORK_OBJECTS.ALREADY_IN_USE',
                    intl: intl,
                  });
                } else {
                  errorToast({
                    body: err.response.data?.error,
                    intl: intl,
                  });
                }
              });
          }
        },
      });
    }
  };

  const onCreateObject = (updatedObject) => {
    fetchData();
    setShowModal(false);
    if (Object.keys(selectedObject).length !== 0) {
      const selectedItem = selectedItems.find(
        (item) => item.id === selectedObject.id
      );
      if (selectedItem) {
        selectedItem.name = updatedObject.name;
      }
    }
  };

  const onHide = () => {
    setEditMode(false);
    setSelectedObject({});
    setShowModal(false);
  };

  const cloudSslVpnCondition =
    typeof props.cloudSslVpnProfile === typeof undefined ||
    selectedType === 'cloud_ssl_vpn';

  return (
    <>
      <Modal
        show={props.showModal}
        onHide={handleHide}
        size="lg"
        centered
        dialogClassName="modal-shadow-lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            <FormattedMessage
              id={
                props.modalTitle ||
                `NETWORK_OBJECTS.${props.selectType.toUpperCase()}_SELECT`
              }
            />
          </Modal.Title>
        </Modal.Header>

        <Modal.Body className="pt-3">
          <div className="w-100 d-flex align-items-center">
            <div className="w-100">
              <BeSafeSearchBar onSearch={setQuery} />
            </div>
            {!props.objectType && (
              <>
                {selectedType !== 'geolocation' && cloudSslVpnCondition && (
                  <BeSafeButton
                    variant="transparent"
                    className="py-0 ml-auto px-1"
                    onClick={showNewObjectModal}
                    icon={<Add />}
                    tooltip="GENERAL.ADD"
                  />
                )}
                <Form.Group className="mr-2 mb-0">
                  <Form.Control
                    className="w-auto"
                    as="select"
                    value={selectedType}
                    onChange={(e) => changeObject(e)}
                  >
                    {allowedNetworks.map(
                      (item, index) =>
                        (props.selectType !== 'destination' ||
                          !item.match(/^(user|user-group)$/)) && (
                          <option key={index} value={item}>
                            {intl.formatMessage({
                              id: getAllowedNetworksText(
                                props.selectType,
                                item
                              ),
                            })}
                          </option>
                        )
                    )}
                  </Form.Control>
                </Form.Group>
              </>
            )}
          </div>
          <BeSafeSeparator />
          <TableLoader rows={8} visible={loading} />
          <NoDataDisplay visible={!loading && items.length <= 0} />
          {!loading && items.length > 0 && (
            <div className="modal-selection-list overflow-auto">
              <Table className="mb-0 table-borderless" hover>
                <thead>
                  <tr>
                    <th>
                      <FormattedMessage id={`TABLE_HEADERS.NAME`} />
                    </th>
                    {selectedType === 'user' && (
                      <th>
                        <FormattedMessage id={`TABLE_HEADERS.MAC_ADDRESSES`} />
                      </th>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {items.map((item, index) => (
                    <tr key={index}>
                      <td className="border-bottom">
                        <div className="d-flex justify-content-between">
                          <Form.Group className="mb-0 d-flex justify-content-between align-items-center w-100">
                            <Form.Check
                              id={item.id}
                              className="mb-0"
                              type="checkbox"
                              onClick={() => selectItemOnClick(item)}
                            >
                              <Form.Check.Input
                                type="checkbox"
                                isValid
                                value={selectedItems.some(
                                  (si) =>
                                    si.id === item.id && si.name === item.name
                                )}
                                checked={selectedItems.some(
                                  (si) =>
                                    si.id === item.id && si.name === item.name
                                )}
                                onClick={(e) => e.stopPropagation()}
                                onChange={(e) => selectRows(item)}
                                disabled={isItemDisabled(item)}
                              />
                              <Form.Check.Label
                                onClick={(e) => e.stopPropagation()}
                              >
                                <i
                                  className={`${
                                    objectDatas[item.type]
                                      ? objectDatas[item.type].icon
                                      : objectDatas['default'].icon
                                  } mr-1`}
                                ></i>
                                {props.tooltipData ? (
                                  <BeSafeTooltip
                                    tooltip={getTooltip(
                                      item,
                                      props.tooltipData
                                    )}
                                  >
                                    <span className="d-inline-block">
                                      {getItemName(item)}
                                    </span>
                                  </BeSafeTooltip>
                                ) : (
                                  <span>{getItemName(item)}</span>
                                )}
                                {item.type === 'application-site-category' &&
                                item.name === 'Very Low Risk' ? (
                                  <div className="badge badge-success ml-2">
                                    1
                                  </div>
                                ) : null}
                                {item.type === 'application-site-category' &&
                                item.name === 'Low Risk' ? (
                                  <div className="badge badge-warning ml-2">
                                    2
                                  </div>
                                ) : null}
                                {item.type === 'application-site-category' &&
                                item.name === 'Medium Risk' ? (
                                  <div className="badge badge-medium ml-2">
                                    3
                                  </div>
                                ) : null}
                                {item.type === 'application-site-category' &&
                                item.name === 'High Risk' ? (
                                  <div className="badge badge-danger ml-2">
                                    4
                                  </div>
                                ) : null}
                                {item.type === 'application-site-category' &&
                                item.name === 'Critical Risk' ? (
                                  <div className="badge badge-very-high ml-2">
                                    5
                                  </div>
                                ) : null}
                              </Form.Check.Label>
                            </Form.Check>
                            {item.editable && cloudSslVpnCondition && (
                              <div>
                                {selectedType !== 'fqdn' &&
                                  selectedType !== 'mac' && (
                                    <BeSafeButton
                                      variant="transparent"
                                      className="p-0 px-1"
                                      onClick={() => showEditObjectModal(item)}
                                      icon={<Edit className="icon-table-md" />}
                                      tooltip="GENERAL.EDIT_SMALL"
                                    />
                                  )}
                                <BeSafeButton
                                  variant="transparent"
                                  className="p-0 px-1"
                                  onClick={() => deleteObject(item)}
                                  icon={<Delete className="icon-table-md" />}
                                  tooltip="GENERAL.DELETE_SMALL"
                                />
                              </div>
                            )}
                          </Form.Group>
                        </div>
                      </td>
                      {selectedType === 'user' && (
                        <td className="border-bottom">
                          {item?.data?.mac_addresses?.join(', ')}
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer className="py-3">
          <Button variant="transparent" onClick={handleHide}>
            <FormattedMessage id="OBJECT_MODAL.CANCEL" />
          </Button>
          <BeSafeButton
            variant="primary"
            type="button"
            onClick={handleSubmit}
            text={<FormattedMessage id="GENERAL.SAVE" />}
          />
        </Modal.Footer>
      </Modal>
      <AddObjectModal
        type={selectedType}
        editMode={editMode}
        showModal={showModal}
        onHide={onHide}
        onCreate={() => onCreateObject()}
        onUpdate={(updatedObject) => onCreateObject(updatedObject)}
        selectedObject={selectedObject}
        selectType={props.selectType}
        cloudSslVpnProfile={props.cloudSslVpnProfile}
        profileId={props.profileId}
      />
    </>
  );
};

export default NetworksListSelect;
